import * as _ from 'underscore';
import { ListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { Helpers } from '../helpers/Helpers';
import { Order } from '../helpers/Order';
import { L10n } from '../L10n/L10n';
import { IGroupInfo } from '../models/IGroupInfo';
import { ISliceable } from '../models/ISliceable';
import { OrderKeys } from '../models/OrderKeys';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResourceQuery } from '../resources/ResourceQuery';
import { SiteUsers } from '../resources/SiteUsers';
import {
  GetUsersGroupMembershipRequest,
  IGetUsersGroupMembershipResult,
} from '../ServerApi/requests/GetUsersGroupMembershipRequest';
import { IFilterClause } from '../ServerApi/types/IFilterClause';
import { IGroupMember } from '../ServerApi/types/IGroupMember';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { IUser, UserKeys } from '../ServerApi/types/IUser';
import { ServerService } from '../services/ServerService';
import { GroupMembershipUpdateActionNotification } from '../services/UserActionNotification';

export interface IUserSearchValue {
  text:string;
}

export interface IAddUsersToGroupDialogScope extends ng.IScope {
  // search stuff
  userSearchValue: IUserSearchValue;
  // site users list stuff
  sliceable: ISliceable<IUser>;
  selectedItems: Set<IUser>; // for grid state
  disabledItems: Set<IUser>; // for grid state
  usersToBeAdded: Set<IUser>; // users to actually add
  usersSliceChanged: (newSlice: ListResult<IUser>) => any;
  rowClicked(row: IUser, colIndex: number): void;
  isRowSelected(locals: { row: IUser }): boolean;
  // dialog stuff
  addUsersToGroupDescription: string;
  confirmLabel: () => string;
  busy: boolean;
  confirm: () => void;
  confirmDisabled?: () => boolean;
  dismiss: () => void;
  close: (result: any) => void;
  errorMessage: string;
}

export class AddUsersToGroupDialogCtrl {

  private isGroupMembershipPrecomputed: boolean = false;

  private static ListOrder: IOrderClause[] = [ Order.asc(OrderKeys.displayName) ];

  public static $inject = [
    '$scope',
    'SiteUsers',
    // Resolved on ui-router state config
    'group',
    'GroupMembershipUpdateActionNotification',
    '$translate',
    'ServerService'
  ];

  constructor(
    private $scope: IAddUsersToGroupDialogScope,
    private users: SiteUsers,
    private group: IGroupInfo,
    private groupMembershipUpdateActionNotification: GroupMembershipUpdateActionNotification,
    private $translate: ng.translate.ITranslateService,
    private server: ServerService
  ) {

    // search stuff

    $scope.userSearchValue = { text: '' };
    $scope.$watch('userSearchValue.text', (newValue: string) => {
      this.onSearchTextChanged(newValue);
    });

    // site users list stuff

    $scope.sliceable = this.users.all().withOrder(AddUsersToGroupDialogCtrl.ListOrder);

    $scope.selectedItems = new Set<IUser>((user: IUser) => user.id);
    $scope.disabledItems = new Set<IUser>((user: IUser) => user.id);
    $scope.usersToBeAdded = new Set<IUser>((user: IUser) => user.id);

    this.tryPrecomputeGroupMembership();

    $scope.usersSliceChanged = (newSlice: ListResult<IUser>) => {
      this.onUsersSliceChanged(newSlice);
    };

    $scope.rowClicked = (row: IUser, colIndex: number) => {
      this.onRowClicked(row, colIndex);
    };

    $scope.isRowSelected = (locals: { row: IUser }) => {
      return $scope.usersToBeAdded.contains(locals.row);
    };

    // dialog stuff

    $scope.addUsersToGroupDescription = this.$translate.instant('addUsersToGroupDescription', { GROUP_NAME: L10n.elideIfLong(group.name) });

    $scope.confirmLabel = () => {
      return this.$translate.instant('confirmAddUsersToGroup', {
        ADD_USER_COUNT: $scope.usersToBeAdded.size()
      }, L10n.Interpolations.Count);
    };

    $scope.busy = false;

    $scope.confirm = () => {
      this.onConfirmAdd();
    };

    $scope.confirmDisabled = () => {
      return ($scope.usersToBeAdded.size() === 0);
    };
  }

  private onSearchTextChanged(newSearchText: string) {
    let query = this.users.all().withOrder(AddUsersToGroupDialogCtrl.ListOrder);
    if (_.isString(newSearchText) && newSearchText.length > 0) {
      query = query.withFilters([Filter.search(newSearchText)]);
    }
    this.$scope.sliceable = query;
  }

  private onConfirmAdd() {
    this.$scope.busy = true;
    const users = _.clone(this.$scope.usersToBeAdded); // clone for independence from further selection

    this.users.addToGroups(users.keys(), [ this.group.id ]).then(
      (result: any) => {

        // show toast
        this.groupMembershipUpdateActionNotification.notify(
          result,
          users,
          1,
          this.group.name,
          0,
          '');

        this.$scope.close(result);
      });
  }

  // Try precompute selection for users already in the group.
  // If there are not that many items in group membership it might make more sense to just
  // precompute the whole group membership. That way, we don't have to issue an extra request
  // everytime the slice changes.
  private tryPrecomputeGroupMembership() {
    const filters:IFilterClause[] = [ Filter.stringArrayFieldHas('groupIds', this.group.id) ];
    const res: IResourceQuery<IUser> = this.users.all().withFilters(filters);
    res.getCount().then((countResult: number) => {
      // only precompute if total group membership is under 100
      if (countResult <= 100) {
        res.getSlice(0, countResult).then((result: ListResult<IUser>) => {
          this.isGroupMembershipPrecomputed = true;
          _.each(result.items, (item: IUser) => {
            this.$scope.selectedItems.add(item);
            this.$scope.disabledItems.add(item);
          });
        });
      } else {
        this.isGroupMembershipPrecomputed = false;
      }
    });
  }

  private onRowClicked(row: IUser, colIndex: number) {
    if (this.$scope.disabledItems.contains(row)) {
      return;
    }

    this.$scope.selectedItems.toggle(row);
    this.$scope.usersToBeAdded.toggle(row);
  }

  // slice changes when the users scrolls down the list
  private onUsersSliceChanged(newSlice: ListResult<IUser>) {
    // only do this if we have not already computed the whole group membership upfront
    if (!this.isGroupMembershipPrecomputed) {
      const userIds:string[] = _.map(newSlice.items, (item: IUser) => { return item.id; });
      const usersByIdMap:_.Dictionary<IUser> = Helpers.dictFromArray<IUser>(newSlice.items, UserKeys.id);

      if (userIds.length > 0) {
        this.server.sendRequest(new GetUsersGroupMembershipRequest({ userIds: userIds }))
          .then((result: IGetUsersGroupMembershipResult) => {
            _.each(result.members, (member: IGroupMember) => {
              if (member.groupId === this.group.id) {
                this.$scope.selectedItems.add(usersByIdMap[member.userId]);
                this.$scope.disabledItems.add(usersByIdMap[member.userId]);
              }
            });
          });
      }
    }
  }

}

Module.instance().controller('AddUsersToGroupDialogCtrl', AddUsersToGroupDialogCtrl);
